import {
  AllCommunityModule,
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
  PivotModule
} from 'ag-grid-enterprise';
import { provideGlobalGridOptions, themeQuartz } from 'ag-grid-community';

/**
 * Quartz with every colour taken from the portal's `--cw-*` tokens.
 *
 * The `--ag-*` overrides in `_ag-grid.scss` sit on `.ag-root-wrapper`, the same
 * one-class specificity as the stylesheet the Theming API injects, and that is
 * injected later - so on a grid with no `theme` prop Quartz's own white rows,
 * inputs and side bar win, which is what shows in dark mode. Passing the tokens
 * as theme params puts them in the injected sheet itself, so the grid follows
 * `data-theme` with nothing left to lose the cascade.
 */
export const portalGridTheme = themeQuartz.withParams({
  backgroundColor: 'var(--cw-surface)',
  foregroundColor: 'var(--cw-text)',
  textColor: 'var(--cw-text)',
  subtleTextColor: 'var(--cw-text-subtle)',
  borderColor: 'var(--cw-border)',
  rowBorder: { color: 'var(--cw-grid-rule)' },
  headerBackgroundColor: 'var(--cw-grid-header)',
  headerTextColor: 'var(--cw-text-muted)',
  chromeBackgroundColor: 'var(--cw-grid-header)',
  rowHoverColor: 'var(--cw-grid-hover)',
  selectedRowBackgroundColor: 'var(--cw-blue-12)',
  accentColor: 'var(--cw-accent)',
  inputBackgroundColor: 'var(--cw-surface)',
  inputBorder: { color: 'var(--cw-border)' },
  inputTextColor: 'var(--cw-text)',
  menuBackgroundColor: 'var(--cw-surface-raised, var(--cw-surface))',
  menuTextColor: 'var(--cw-text)',
  panelBackgroundColor: 'var(--cw-surface)',
  fontFamily: "'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, Arial, sans-serif",
  fontSize: 12,
  headerHeight: 38,
  rowHeight: 40,
  wrapperBorder: false,
  wrapperBorderRadius: 0
});

/**
 * ag-grid Enterprise licence for Cushman & Wakefield Global, Inc.
 * Single developer, one production deployment.
 */
const AG_GRID_LICENSE_KEY =
  'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-078719}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Cushman_&_Wakefield_Global,_Inc}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{18_June_2026}____[v3]_[0102]_MTc4MTczNzIwMDAwMA==3d83d461ffd91d4aa36e813251f26c82';

/** The page sizes the portal offers everywhere, server-paged lists included. */
export const GRID_PAGE_SIZES = [25, 50, 100];

/**
 * Defaults every grid starts from, so theme, paging and the footer read the same
 * on every screen. A grid still overrides any of them on its own props - the
 * server-paged and spreadsheet-style grids turn client paging off.
 */
/*
 * The grid's loading overlay, as list-row skeletons - the loading system's
 * "no bare spinners in the content area" rule applied to every grid at once.
 * A template string rather than a React component so it can live in the
 * global options; same `cw-skel` classes as `<SkeletonRows>`, at the grid's
 * 40px row height (`.cw-skel-rows--grid` in _ag-grid.scss).
 */
const SKELETON_ROW =
  '<div class="cw-skel-row">' +
  '<span class="cw-skel cw-skel--circle" style="width:10px;height:10px"></span>' +
  '<span class="cw-skel-row__main"><span class="cw-skel" style="width:%W%;height:9px"></span></span>' +
  '<span class="cw-skel cw-skel--block cw-skel-row__cell" style="width:76px;height:14px"></span>' +
  '<span class="cw-skel cw-skel-row__cell" style="width:92px;height:8px"></span>' +
  '<span class="cw-skel cw-skel-row__cell" style="width:44px;height:8px"></span>' +
  '</div>';

const GRID_LOADING_TEMPLATE =
  '<div class="cw-skel-rows cw-skel-rows--grid" role="status" aria-label="Loading rows">' +
  ['52%', '44%', '60%', '48%', '56%'].map((width) => SKELETON_ROW.replace('%W%', width)).join('') +
  '</div>';

/*
 * Filtering is the Advanced Filter on every grid: one builder where conditions
 * on the same column or on different columns are combined with AND and OR, and
 * nested in groups. It replaces the per-column header filters, so a grid that
 * filters through its own panel or on the server turns it off on its props.
 */
const PORTAL_GRID_OPTIONS = {
  theme: portalGridTheme,
  enableAdvancedFilter: true,
  includeHiddenColumnsInAdvancedFilter: true,
  advancedFilterBuilderParams: { showMoveButtons: true },
  overlayLoadingTemplate: GRID_LOADING_TEMPLATE,
  pagination: true,
  paginationPageSize: GRID_PAGE_SIZES[0],
  paginationPageSizeSelector: GRID_PAGE_SIZES,
  statusBar: {
    statusPanels: [
      { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
      { statusPanel: 'agSelectedRowCountComponent' },
      { statusPanel: 'agAggregationComponent' }
    ]
  }
};

/**
 * Registers the ag-grid feature modules and licence once, for the whole app.
 *
 * This has to happen before the first grid is created. Screens are lazy loaded,
 * so registering from inside a feature component would only cover grids that
 * happen to load after that feature; every other grid renders empty with
 * ag-grid error #272. Called from `main.ts` before the app bootstraps.
 *
 * Integrated charts are deliberately not registered here. They pull in
 * ag-charts Enterprise (~3 MB) and only two screens use them, so they register
 * themselves through `configureAgGridCharts()`.
 */
export function configureAgGrid(): void {
  ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule, PivotModule]);
  LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
  provideGlobalGridOptions(PORTAL_GRID_OPTIONS);
}
